import { useTranslation } from 'next-i18next'
import React from 'react'

import { TOTAL_STEPS, WizardStep } from './types'

interface IProps {
  step: WizardStep
}

/**
 * Slim hairline progress strip + "Paso X de N" indicator. Visible from
 * step 1 onwards so the sunk-cost commitment is legible — the user sees
 * their own progress fill across the strip as they advance.
 *
 * Editorial vocabulary: 2px hairline track, blue-500 fill, Fraunces italic
 * step number. No animation between steps beyond the natural CSS width
 * transition; nothing fancy that could feel janky on slow hardware.
 */
const ProgressBar = ({ step }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const pct = Math.round((step / TOTAL_STEPS) * 100)

  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <span className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
          {t('WIZARD.EYEBROW')}
        </span>
        <span className="font-display text-[12px] font-medium text-ink-200 italic">
          {t('WIZARD.STEP_INDICATOR', { current: step, total: TOTAL_STEPS })}
        </span>
      </div>
      <div
        className="relative h-[2px] w-full overflow-hidden rounded-full bg-ink-100/15"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="absolute inset-y-0 left-0 bg-blue-500 transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

export default React.memo(ProgressBar)
